import { type ComponentProps, forwardRef } from 'react';

import { type VariantProps, cva } from 'class-variance-authority';
import { Toast as ToastPrimitives } from 'radix-ui';

import X from '@quick/assets/icons/close.svg';
import { cn } from '@quick/utils';

const ToastProvider = ToastPrimitives.Provider;

function ToastViewport({
  className,
  ref,
  ...props
}: ComponentProps<typeof ToastPrimitives.Viewport>) {
  return (
    <ToastPrimitives.Viewport
      ref={ref}
      className={cn(
        `fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4
        sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]`,
        className
      )}
      {...props}
    />
  );
}
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  [
    'data-[swipe=move]:transition-none group relative pointer-events-auto flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full data-[state=closed]:slide-out-to-right-full dark:text-white',
    'bg-background-light dark:bg-background-dark',
  ],
  {
    variants: {
      variant: {
        default: '',
        success:
          'border-success group success bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
        danger:
          'border-danger group danger bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
        warning:
          'border-yellow-500 group warning bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-50',
        info: 'border-primary group info bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
        promise:
          'border-gray-700 group promise bg-bg text-gray-800 dark:text-gray-200',
        // destructive:
        //   'group destructive border-destructive bg-destructive text-destructive-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Toast = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      `ring-offset-background hover:bg-background-group
      hover:dark:bg-background-group-dark focus:ring-ring inline-flex h-8 shrink-0
      items-center justify-center rounded-md border bg-transparent px-3 text-sm
      font-medium transition-colors focus:outline-none focus:ring-2
      focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50`,
      `group-[.success]:border-success/30 group-[.success]:hover:border-success/30
      group-[.success]:hover:bg-success group-[.success]:hover:text-success-50
      group-[.success]:focus:ring-success`,
      `group-[.danger]:border-danger/30 group-[.danger]:hover:border-danger/30
      group-[.danger]:hover:bg-danger group-[.danger]:hover:text-danger-50
      group-[.danger]:focus:ring-danger`,
      `group-[.warning]:border-yellow-500/30
      group-[.warning]:hover:border-yellow-500/30 group-[.warning]:hover:bg-yellow-500
      group-[.warning]:hover:text-yellow-50 group-[.warning]:focus:ring-yellow-500`,
      `group-[.info]:border-primary/30 group-[.info]:hover:border-primary/30
      group-[.info]:hover:bg-primary group-[.info]:focus:ring-primary
      group-[.info]:hover:text-blue-50`,
      // 'group-[.destructive]:border-destructive/30 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive',
      className
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      `absolute right-2 top-2 rounded-md p-1 text-gray-800/50 opacity-0
      transition-opacity hover:text-gray-800 focus:opacity-100 focus:outline-none
      focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300
      group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400
      group-[.destructive]:focus:ring-offset-red-600 dark:text-gray-200/50
      hover:dark:text-gray-200`,
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn('text-sm font-semibold', className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn(
      'max-h-24 overflow-y-auto whitespace-pre-wrap break-words text-sm opacity-90',
      className
    )}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  type ToastActionElement,
  type ToastProps,
};
